import type {
  PaymentAmounts,
  PaymentCollectionStatus,
  PaymentDepositRefundStatus,
  PaymentRefundReason,
  PaymentSettlementStatus,
  PaymentTab,
} from '@/types/payment.types';
import type { PayoutMethod } from '@/types/payout.types';

export const PAGE_SIZE = 20;
export const PAYMENTS_QUERY_KEY = 'payments';

export const PAYMENT_TABS: { key: PaymentTab; label: string }[] = [
  { key: 'awaiting', label: 'Awaiting' },
  { key: 'submitted', label: 'Submitted' },
  { key: 'verified', label: 'Verified' },
  { key: 'refund_due', label: 'Refund due' },
  // Refund-due records live on here once actioned; without it they matched no tab and
  // dropped out of the panel entirely.
  { key: 'closed', label: 'Closed' },
  { key: 'to_settle', label: 'To settle' },
  { key: 'settled', label: 'Settled' },
  { key: 'disputed', label: 'Disputed' },
];

export const COLLECTION_STATUS_LABEL: Record<PaymentCollectionStatus, { label: string; tone: string }> = {
  awaiting_payment: { label: 'Awaiting payment', tone: 'bg-amber-100 text-amber-700' },
  payment_submitted: { label: 'Submitted', tone: 'bg-brand-50 text-brand-600' },
  verified: { label: 'Verified', tone: 'bg-emerald-100 text-emerald-700' },
  refund_due: { label: 'Refund due', tone: 'bg-red-100 text-red-700' },
  rejected: { label: 'Rejected', tone: 'bg-red-100 text-red-700' },
  // Closed without collecting: the booking ended before the borrower paid, so there is nothing
  // owed either way. Neutral tone — it is not a failure anyone needs to act on.
  cancelled_unpaid: { label: 'Cancelled unpaid', tone: 'bg-slate-100 text-slate-600' },
  refunded: { label: '✓ Refunded', tone: 'bg-emerald-100 text-emerald-700' },
};

/**
 * Why the money is going back — the short line on the row and the drawer banner.
 *
 * The three owner-fault reasons are worded to read as such at a glance: they are the ones
 * where a student paid and the owner ended it, which is what makes a repeat offender visible
 * in the queue without opening a single record.
 */
export const REFUND_REASON_LABEL: Record<PaymentRefundReason, string> = {
  owner_no_response: 'Owner never responded',
  owner_declined: 'Owner declined after payment',
  owner_cancelled: 'Owner cancelled after accepting',
  borrower_cancelled: 'Borrower cancelled',
  cancelled: 'Cancelled after payment',
};

/** Reasons where the owner ended a booking the borrower had already paid for. */
export const OWNER_FAULT_REASONS: PaymentRefundReason[] = [
  'owner_no_response',
  'owner_declined',
  'owner_cancelled',
];

export const REFUND_DUE_BANNER =
  'This booking fell through with the money already collected. Transfer it back to the borrower, then record it here.';
export const REFUND_UNVERIFIED_WARNING =
  'This payment was never verified against the bank. Check the statement first — if the money never arrived, close it as never received instead of refunding.';
export const REFUND_MISSING_PAYOUT =
  'This borrower has no payout details and gave no UPI ID with their proof. Ask for them before recording the refund.';
export const REFUND_CONFIRM_LABEL = 'Confirm refund sent';
/**
 * What the settlement column shows for a booking that fell through. There is no owner payout
 * to make — the rental never happened — so the settlement leg sits at its initial `pending`
 * for good. Printing "Pending" there read as outstanding work that would never clear.
 */
export const SETTLEMENT_NOT_APPLICABLE = {
  label: 'No payout — refunded',
  tone: 'bg-[var(--color-surface-muted)] text-[var(--color-ink-muted)]',
};
/** Shown once the borrower has acknowledged the money actually landed back with them. */
export const REFUND_CONFIRMED_LABEL = {
  label: '✓ Refund confirmed',
  tone: 'bg-emerald-100 text-emerald-700',
};
export const REFUND_NOT_RECEIVED_LABEL = 'No payment was received';

export const SETTLEMENT_STATUS_LABEL: Record<PaymentSettlementStatus, { label: string; tone: string }> = {
  pending: { label: 'Pending', tone: 'bg-[var(--color-surface-muted)] text-[var(--color-ink-muted)]' },
  paid: { label: 'Paid — awaiting owner', tone: 'bg-brand-50 text-brand-600' },
  owner_confirmed: { label: '✓ Owner confirmed', tone: 'bg-emerald-100 text-emerald-700' },
  disputed: { label: 'Disputed', tone: 'bg-red-100 text-red-700' },
};

export const DEPOSIT_STATUS_LABEL: Record<PaymentDepositRefundStatus, { label: string; tone: string }> = {
  pending: { label: 'Deposit pending', tone: 'bg-[var(--color-surface-muted)] text-[var(--color-ink-muted)]' },
  refunded: { label: 'Refunded — awaiting borrower', tone: 'bg-brand-50 text-brand-600' },
  borrower_confirmed: { label: '✓ Borrower confirmed', tone: 'bg-emerald-100 text-emerald-700' },
  forfeited: { label: 'Deposit forfeited', tone: 'bg-red-100 text-red-700' },
};

/** Label for a party's payout rail in the detail drawer. */
export const PAYOUT_METHOD_LABEL: Record<PayoutMethod, string> = {
  upi: 'UPI',
  bank: 'Bank transfer',
};

export const PAYOUT_MISSING_OWNER =
  'This owner has not added payout details, so there is nowhere to send the money. Remind them, then settle once they have.';
export const PAYOUT_MISSING_BORROWER =
  'This borrower has not added payout details and gave no UPI ID with their proof. Remind them before refunding the deposit.';
export const PAYOUT_REMIND_LABEL = 'Ask for payout details';

/**
 * Human copy for every payment-record event, replacing the raw slug the timeline used to
 * print ("refund due", "deposit confirmed"). Each line has to answer "what happened, to whose
 * money" on its own, because this trail is what an admin reads when a student disputes
 * something months later.
 *
 * Keys are the event strings written by paymentRecord.repository.ts. An unknown key falls back
 * to the de-underscored slug, so a new event degrades rather than disappearing.
 */
export const PAYMENT_EVENT_LABEL: Record<string, string> = {
  created: 'Payment raised',
  payment_submitted: 'Borrower submitted their payment proof',
  verified: 'Payment verified against the bank',
  rejected: 'Payment proof rejected',
  refund_due: 'Refund owed to the borrower',
  refunded: 'Refund transferred to the borrower',
  refund_not_owed: 'Closed — no payment ever arrived',
  ready_to_settle: 'Rental completed — payout unlocked',
  settled: 'Payout transferred to the owner',
  owner_confirmed: 'Owner confirmed they received their payout',
  deposit_refunded: 'Deposit returned to the borrower',
  deposit_confirmed: 'Borrower confirmed they received it',
  disputed: 'Owner disputed their payout',
};

/** Which events are worth printing an amount beside, and which amount that is. */
export const PAYMENT_EVENT_AMOUNT: Record<string, keyof PaymentAmounts> = {
  created: 'borrowerTotalPaise',
  payment_submitted: 'borrowerTotalPaise',
  verified: 'borrowerTotalPaise',
  refund_due: 'borrowerTotalPaise',
  refunded: 'borrowerTotalPaise',
  settled: 'ownerPayoutPaise',
  deposit_refunded: 'depositPaise',
};
